import { useState, useMemo } from "react";
import { FaTrash, FaPlus, FaCloudUploadAlt, FaEdit } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  TextInput,
  Button,
  Select,
  Checkbox,
  MultiSelect,
  Group,
  NumberInput,
} from "@mantine/core";
import { useGetChannelVideosQuery } from "../../features/api/youtubeApi";
import FuelLoading from "../../pages/FuelLoading";
import "./adminProduct.scss";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useSyncPrintifyProductsMutation,
  useGetAdminProductsQuery,
} from "../../features/api/ProductApiSlice";

import PrintifyProductEditor from "./PrintifyProductEditor";

const CATEGORY_OPTIONS = [
  "Small Car Essentials",
  "Sleep & Comfort",
  "Filming the Journey",
  "Safety & Tech",
  "Apparel",
  "Merchandise",
];

const PRODUCT_TYPES = [
  {
    value: "affiliate_recommendation",
    label: "🔗 Affiliate Gear Recommendation",
  },
  {
    value: "branded_merchandise",
    label: "🚗 Branded Store Merchandise (Direct Sale)",
  },
];

const AFFILIATE_PLATFORMS = [
  { value: "amazon", label: "Amazon" },
  { value: "bhphoto", label: "B&H Photo" },
  { value: "rei", label: "REI" },
  { value: "other", label: "Other" },
];

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

export default function AdminProduct() {
  const { data: productsList = [], isLoading: productLoading } =
    useGetAdminProductsQuery();
  const { data: videosData, isLoading: videosLoading } =
    useGetChannelVideosQuery({ search: "", page: 1, limit: 100 });

  const videosArray = useMemo(() => {
    if (!videosData) return [];
    if (Array.isArray(videosData)) return videosData;
    if (Array.isArray(videosData.items)) return videosData.items;
    return [];
  }, [videosData]);

  const videoOptions = useMemo(
    () =>
      videosArray.map((v) => ({
        value: v._id || v.youtubeId || String(v.id),
        label: v.title || "(untitled)",
      })),
    [videosArray]
  );

  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();

  // PRINTIFY: Manual product synchronization.
  const [syncPrintifyProducts, { isLoading: isSyncingPrintify }] =
    useSyncPrintifyProductsMutation();

  const [printifySyncResult, setPrintifySyncResult] = useState(null);
  const [printifySyncError, setPrintifySyncError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [selectedPrintifyId, setSelectedPrintifyId] = useState(null);

  // Always retrieve the latest product from the admin catalog.
  // This ensures publication changes appear after RTK Query refetches.
  const selectedPrintifyProduct = productsList.find(
    (product) => product._id === selectedPrintifyId
  );

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // 🔥 THE FIX: Setup main product state form targeting the new variants format
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "",
    price: 0,
    priceNote: "",
    productType: "affiliate_recommendation",
    featured: false,
    inStock: true,
    tags: [],
    affiliateLinks: [],
    usedInVideos: [],
    variants: [], // Subdocument Array: [{ size, colorName, sku, countInStock, colorImages: [] }]
  });

  // 🔥 VARIANT GENERATOR WORKBENCH STATE LAYER
  const [vSize, setVSize] = useState("");
  const [vColor, setVColor] = useState("");
  const [vVolume, setVVolume] = useState("");
  const [vSku, setVSku] = useState("");
  const [vStock, setVStock] = useState(10);

  // Maps tracking file blobs and previews relative to their row index
  const [variantFilesMap, setVariantFilesMap] = useState({});
  const [variantPreviewsMap, setVariantPreviewsMap] = useState({});

  // Base product fallback file attachments (used if no options exist)
  const [baseFiles, setBaseFiles] = useState([]);
  const [basePreviews, setBasePreviews] = useState([]);

  const isMutating = isCreating || isUpdating;

  // Handle fallback file changes
  const handleBaseFileChange = (e) => {
    const files = Array.from(e.target.files);
    setBaseFiles(files);
    setBasePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  // 🔥 NEW: Map local pictures to specific variant index coordinates
  const handleVariantFileChange = (index, e) => {
    const files = Array.from(e.target.files);
    setVariantFilesMap((prev) => ({ ...prev, [index]: files }));
    setVariantPreviewsMap((prev) => ({
      ...prev,
      [index]: files.map((f) => URL.createObjectURL(f)),
    }));
  };

  // 🔥 NEW: Create options array profiles cleanly into staging array matrix
  const handleAddVariantToStaging = () => {
    if (!vSku.trim()) {
      alert("A unique SKU code tracking identifier is required!");
      return;
    }

    const isDuplicateSku = form.variants.some(
      (v) => v.sku.toLowerCase() === vSku.trim().toLowerCase()
    );
    if (isDuplicateSku) {
      alert("This SKU identifier is already assigned to another variant row!");
      return;
    }

    const nextVariantRow = {
      size: vSize.trim(),
      colorName: vColor.trim(),
      volume: vVolume.trim(),
      sku: vSku.trim().toUpperCase(),
      countInStock: parseInt(vStock) || 0,
      colorImages: [], // Loaded on backend via multipart streams mapping
    };

    setForm({
      ...form,
      variants: [...form.variants, nextVariantRow],
    });

    // Reset input fields
    setVSize("");
    setVColor("");
    setVVolume("");
    setVSku("");
    setVStock(10);
  };

  const handleRemoveVariantFromStaging = (index) => {
    const updatedVariants = form.variants.filter((_, i) => i !== index);
    setForm({ ...form, variants: updatedVariants });

    // Clean up files indices caches safely
    const nextFilesMap = { ...variantFilesMap };
    const nextPreviewsMap = { ...variantPreviewsMap };
    delete nextFilesMap[index];
    delete nextPreviewsMap[index];
    setVariantFilesMap(nextFilesMap);
    setVariantPreviewsMap(nextPreviewsMap);
  };

  const updateAffiliateLink = (index, field, value) => {
    const updated = [...form.affiliateLinks];
    updated[index][field] = value;
    setForm({ ...form, affiliateLinks: updated });
  };

  const resetForm = () => {
    setEditingId(null);
    setBaseFiles([]);
    setBasePreviews([]);
    setVariantFilesMap({});
    setVariantPreviewsMap({});
    setError("");
    setForm({
      name: "",
      description: "",
      category: "",
      price: 0,
      priceNote: "",
      productType: "affiliate_recommendation",
      featured: false,
      inStock: true,
      tags: [],
      affiliateLinks: [],
      usedInVideos: [],
      variants: [],
    });
  };

  const handlePrintifySync = async () => {
    if (isSyncingPrintify) return;

    setPrintifySyncError("");
    setPrintifySyncResult(null);

    try {
      const result = await syncPrintifyProducts().unwrap();

      setPrintifySyncResult(result);
    } catch (error) {
      setPrintifySyncError(
        error?.data?.message ||
          error?.error ||
          "Unable to synchronize Printify products."
      );
    }
  };

  // 🔥 SUBMIT ENGINE WITH RECONFIGURED VARIANTMULTIPART MAPPINGS
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name.trim() || !form.description.trim() || !form.category) {
      setError("Please complete all essential product fields configurations.");
      return;
    }

    const formData = new FormData();
    formData.append("name", form.name.trim());
    formData.append("description", form.description.trim());
    formData.append("category", form.category);
    formData.append("productType", form.productType);
    formData.append("price", String(form.price));
    formData.append("priceNote", form.priceNote.trim());
    formData.append("featured", String(form.featured));
    formData.append("inStock", String(form.inStock));
    formData.append("tags", form.tags.join(","));
    formData.append("affiliateLinks", JSON.stringify(form.affiliateLinks));

    form.usedInVideos.forEach((vId) => formData.append("usedInVideos", vId));

    // 🔥 THE CRITICAL FIX: Append files as variantImages_0, variantImages_1, etc.
    if (form.variants.length > 0) {
      form.variants.forEach((variantObj, idx) => {
        const filesForThisVariant = variantFilesMap[idx] || [];
        filesForThisVariant.forEach((file) => {
          // This matches the exact dynamic key field structure on the backend parser loop!
          formData.append(`variantImages_${idx}`, file);
        });
      });
      formData.append("variants", JSON.stringify(form.variants));
    } else {
      // Fallback to base product pictures if product is simple or affiliate link
      baseFiles.forEach((file) => {
        formData.append("productImages", file);
      });
      formData.append("variants", JSON.stringify([]));
    }

    try {
      if (editingId) {
        await updateProduct({ id: editingId, formData }).unwrap();
        setSuccess("🎉 Product entry updated successfully!");
      } else {
        await createProduct(formData).unwrap();
        setSuccess("🎉 Fresh e-commerce listing published across catalogs!");
      }
      resetForm();
      setTimeout(() => setSuccess(""), 3500);
    } catch (err) {
      setError(
        "⚠️ Failed to synchronize product parameters mapping with backend server nodes."
      );
    }
  };

  const handleManagePrintify = (product) => {
    setSelectedPrintifyId(product._id);

    // Clear any active self-fulfilled product editing session.
    resetForm();

    setSuccess("");
    setError("");
  };

  const handleEditSelect = (product) => {
    // This ensures clicking Edit on a self-fulfilled product switches back to the original editor.
    setSelectedPrintifyId(null);
    setEditingId(product._id);
    setForm({
      name: product.name || "",
      description: product.description || "",
      category: product.category || "",
      price: product.price || 0,
      priceNote: product.priceNote || "",
      productType: product.productType || "affiliate_recommendation",
      featured: product.featured || false,
      inStock: product.inStock !== undefined ? product.inStock : true,
      tags: product.tags || [],
      affiliateLinks: product.affiliateLinks || [],
      usedInVideos:
        product.usedInVideos?.map((v) => (typeof v === "string" ? v : v._id)) ||
        [],
      variants: Array.isArray(product.variants) ? product.variants : [],
    });

    if (Array.isArray(product.variants) && product.variants.length > 0) {
      const initialPreviews = {};
      product.variants.forEach((v, i) => {
        initialPreviews[i] = v.colorImages || [];
      });
      setVariantPreviewsMap(initialPreviews);
    } else {
      setBasePreviews(product.images || []);
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to permanently delete this item from public view feeds?"
      )
    ) {
      try {
        await deleteProduct(id).unwrap();
        setSuccess("🗑️ Product removed from catalogue files successfully.");
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        setError("Failed to complete record deletion query.");
      }
    }
  };

  if (productLoading) {
    return (
      <div className="admin-notes-loading-box">
        <FuelLoading text="Opening catalog inventory boxes..." />
      </div>
    );
  }

  return (
    <div className="admin-gear-dashboard fuel-fade-in">
      <div className="admin-gear-layout-grid">
        {/* COMPACT PRODUCT CONFIGURATION EDITOR CONTROL PANEL */}
        {/* LEFT COLUMN: STANDARD OR PRINTIFY PRODUCT EDITOR */}
        <section className="gear-editor-panel">
          {selectedPrintifyProduct ? (
            <PrintifyProductEditor
              key={selectedPrintifyProduct._id}
              product={selectedPrintifyProduct}
              onClose={() => setSelectedPrintifyId(null)}
            />
          ) : (
            <>
              <h2>
                {editingId
                  ? "✏️ Edit Store Listing"
                  : "🛒 Add Store E-Commerce Product"}
              </h2>
              <p>
                Structure custom sizing variables, assign direct store pricing
                parameters, or link affiliate codes.
              </p>

              <form onSubmit={handleSubmit} className="gear-editor-form">
                {success && (
                  <div className="notes-alert notes-alert--success">
                    {success}
                  </div>
                )}
                {error && (
                  <div className="notes-alert notes-alert--error">{error}</div>
                )}

                <Select
                  label="Listing Store Mode Model *"
                  data={PRODUCT_TYPES}
                  value={form.productType}
                  onChange={(val) => setForm({ ...form, productType: val })}
                />

                <TextInput
                  label="Gear / Merchandise Title *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />

                <div className="quill-wrapper-block">
                  <span className="quill-label-text">
                    Product Rich Text Description *
                  </span>
                  <ReactQuill
                    theme="snow"
                    value={form.description}
                    onChange={(val) => setForm({ ...form, description: val })}
                    modules={quillModules}
                    placeholder="Describe product fabric specs, dimensions, or usage parameters..."
                  />
                </div>

                <Select
                  label="Category Tag *"
                  data={CATEGORY_OPTIONS}
                  value={form.category}
                  onChange={(val) => setForm({ ...form, category: val })}
                  required
                />

                <TextInput
                  label="Tags Layout (Comma separated values)"
                  placeholder="shirt, outer, canvas, water-proof"
                  value={form.tags.join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tags: e.target.value.split(",").map((t) => t.trim()),
                    })
                  }
                />

                <div className="form-split-row-matrix">
                  {form.productType === "branded_merchandise" ? (
                    <NumberInput
                      label="Direct Selling Price ($ USD) *"
                      decimalScale={2}
                      value={form.price}
                      onChange={(val) => setForm({ ...form, price: val || 0 })}
                      precision={2}
                      step={0.01}
                      min={0}
                      required
                    />
                  ) : (
                    <TextInput
                      label="Affiliate Retail Note Price"
                      placeholder="e.g. Varies by retailer"
                      value={form.priceNote}
                      onChange={(e) =>
                        setForm({ ...form, priceNote: e.target.value })
                      }
                    />
                  )}

                  <div className="checkboxes-stack-wrapper">
                    <Checkbox
                      label="Featured Store Spotlight"
                      checked={form.featured}
                      onChange={(e) =>
                        setForm({ ...form, featured: e.currentTarget.checked })
                      }
                    />
                    <Checkbox
                      label="In Stock Availability Status"
                      checked={form.inStock}
                      onChange={(e) =>
                        setForm({ ...form, inStock: e.currentTarget.checked })
                      }
                    />
                  </div>
                </div>

                {/* ===== 🔥 NEW FIXED MULTI-VARIANT PARAMETERS MANAGER FACTORY ===== */}
                {form.productType === "branded_merchandise" && (
                  <div
                    className="ecommerce-variants-manager"
                    style={{
                      border: "1px solid rgba(255,255,255,0.06)",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      background: "#0b0b0b",
                      marginTop: "1.5rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <h3
                      style={{ margin: 0, fontSize: "1rem", color: "#f59e0b" }}
                    >
                      E-Commerce Product Variants Configuration
                    </h3>
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "rgba(255,255,255,0.4)",
                        marginTop: "4px",
                        marginBottom: "1rem",
                      }}
                    >
                      Build dynamic size/color combinations. Every distinct row
                      maintains its own stock limits.
                    </p>

                    {/* Subform Variant Generator Construction Block */}
                    <div
                      className="variant-subform-builder-card"
                      style={{
                        background: "#141414",
                        padding: "1rem",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#fff",
                        }}
                      >
                        Configure Option Node Row
                      </span>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "0.5rem",
                        }}
                      >
                        <TextInput
                          placeholder="Size (e.g. M, L)"
                          value={vSize}
                          onChange={(e) => setVSize(e.target.value)}
                        />
                        <TextInput
                          placeholder="Color (e.g. Red)"
                          value={vColor}
                          onChange={(e) => setVColor(e.target.value)}
                        />
                        <TextInput
                          placeholder="Vol (e.g. 32 oz)"
                          value={vVolume}
                          onChange={(e) => setVVolume(e.target.value)}
                        />
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.5fr 0.5fr",
                          gap: "0.5rem",
                        }}
                      >
                        <TextInput
                          placeholder="Unique Code SKU (Required) *"
                          value={vSku}
                          onChange={(e) => setVSku(e.target.value)}
                        />
                        <NumberInput
                          min={0}
                          placeholder="Stock"
                          value={vStock}
                          onChange={(val) => setVStock(val || 0)}
                        />
                      </div>

                      <Button
                        type="button"
                        color="yellow"
                        size="xs"
                        onClick={handleAddVariantToStaging}
                        style={{ marginTop: "4px" }}
                      >
                        <FaPlus style={{ marginRight: "6px" }} /> Stage Variant
                        Combo Row
                      </Button>
                    </div>

                    {/* Realtime Array Grid Staging Output Display */}
                    <div
                      className="staged-variants-viewer-tray"
                      style={{ marginTop: "1.5rem" }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          display: "block",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Active Option Blocks Staging Room (
                        {form.variants.length})
                      </span>

                      {form.variants.length === 0 ? (
                        <p
                          style={{
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            color: "rgba(255,255,255,0.3)",
                          }}
                        >
                          No variations combined yet.
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                          }}
                        >
                          {form.variants.map((variantObj, idx) => (
                            <div
                              key={idx}
                              className="color-upload-row-box"
                              style={{
                                background: "#111",
                                padding: "1rem",
                                borderRadius: "10px",
                                border: "1px solid rgba(255,255,255,0.04)",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                <div style={{ fontSize: "0.85rem" }}>
                                  <strong style={{ color: "#f59e0b" }}>
                                    ⚙️ SKU: {variantObj.sku}
                                  </strong>
                                  <span
                                    style={{
                                      marginLeft: "8px",
                                      color: "rgba(255,255,255,0.6)",
                                    }}
                                  >
                                    ({variantObj.size || "No Size"} /{" "}
                                    {variantObj.colorName || "No Color"} /{" "}
                                    {variantObj.volume || "No Vol"})
                                  </span>
                                  <div
                                    style={{
                                      color: "#10b981",
                                      fontWeight: "bold",
                                      fontSize: "0.75rem",
                                      marginTop: "2px",
                                    }}
                                  >
                                    📦 Staging Warehouse Units Count:{" "}
                                    {variantObj.countInStock}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  color="red"
                                  size="xs"
                                  onClick={() =>
                                    handleRemoveVariantFromStaging(idx)
                                  }
                                >
                                  Remove
                                </Button>
                              </div>

                              {/* Dynamic multipart upload input channel tied explicitly to this coordinate index array frame */}
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                id={`variant-file-${idx}`}
                                onChange={(e) =>
                                  handleVariantFileChange(idx, e)
                                }
                                style={{ display: "none" }}
                              />
                              <label
                                htmlFor={`variant-file-${idx}`}
                                className="save-settings-btn"
                                style={{
                                  textAlign: "center",
                                  display: "block",
                                  padding: "0.4rem",
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  background: "#1c1c1c",
                                  border: "1px solid rgba(255,255,255,0.08)",
                                  borderRadius: "6px",
                                }}
                              >
                                <FaCloudUploadAlt
                                  style={{ marginRight: "6px" }}
                                />{" "}
                                Upload Specific Variant Images
                              </label>

                              {variantPreviewsMap[idx]?.length > 0 && (
                                <div
                                  className="multi-images-preview-tray"
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                    marginTop: "0.5rem",
                                  }}
                                >
                                  {variantPreviewsMap[idx].map((url, i) => (
                                    <img
                                      key={i}
                                      src={url}
                                      alt=""
                                      className="tray-preview-thumb"
                                      style={{
                                        width: "48px",
                                        height: "48px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        border:
                                          "1px solid rgba(255,255,255,0.1)",
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* FALLBACK BASE PICTURES SLOT */}
                {form.variants.length === 0 && (
                  <div className="video-selector-zone">
                    <h3>Product Fallback Visual Assets Vault</h3>
                    <p>
                      Select up to 5 product photos simultaneously. Previews
                      display horizontally beneath.
                    </p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      id="ecommerce-base-files"
                      onChange={handleBaseFileChange}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="ecommerce-base-files"
                      className="save-settings-btn"
                      style={{
                        textAlign: "center",
                        display: "block",
                        padding: "0.6rem",
                        cursor: "pointer",
                      }}
                    >
                      📷 Choose Base Product Photos
                    </label>
                    {basePreviews.length > 0 && (
                      <div className="multi-images-preview-tray">
                        {basePreviews.map((url, i) => (
                          <img
                            key={i}
                            src={url}
                            alt=""
                            className="tray-preview-thumb"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* EXTERNAL REFERRAL RETAILERS LINKS PANEL */}
                {form.productType === "affiliate_recommendation" && (
                  <div
                    className="video-selector-zone"
                    style={{ background: "#0b0b0b" }}
                  >
                    <h3>Affiliate Distribution Outlets</h3>
                    <p>
                      Add secure referral targets mapped across distinct outdoor
                      retailers lists.
                    </p>
                    {form.affiliateLinks.map((link, index) => (
                      <Group key={index} mt="xs" grow>
                        <Select
                          data={AFFILIATE_PLATFORMS}
                          value={link.platform}
                          onChange={(val) =>
                            updateAffiliateLink(index, "platform", val)
                          }
                        />
                        <TextInput
                          placeholder="Paste link URL track parameters..."
                          value={link.url}
                          onChange={(e) =>
                            updateAffiliateLink(index, "url", e.target.value)
                          }
                        />
                      </Group>
                    ))}
                    <Button
                      variant="outline"
                      size="xs"
                      mt="sm"
                      onClick={() =>
                        setForm({
                          ...form,
                          affiliateLinks: [
                            ...form.affiliateLinks,
                            { platform: "amazon", url: "" },
                          ],
                        })
                      }
                    >
                      + Add Affiliate Retailer Route
                    </Button>
                  </div>
                )}

                <MultiSelect
                  label="Featured Inside Channel Driving Videos"
                  data={videoOptions}
                  value={form.usedInVideos}
                  onChange={(val) => setForm({ ...form, usedInVideos: val })}
                  searchable
                  clearable
                  loading={videosLoading}
                />

                <div className="editor-actions">
                  <button
                    type="submit"
                    className="fuel-primary-btn submit-note-btn"
                    disabled={isMutating}
                  >
                    {editingId
                      ? "Update Product Listing"
                      : "🚀 Publish Product Entry"}
                  </button>
                  <button
                    type="button"
                    className="cancel-ghost-btn"
                    onClick={resetForm}
                    disabled={isMutating}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </section>

        {/* ===== RIGHT COLUMN SIDE PANEL: ACTIVE INVENTORY CATALOG FEED ===== */}
        <section className="notes-list-panel">
          {/* PRINTIFY PRODUCT SYNCHRONIZATION */}
          <div className="printify-sync-panel">
            <h3 style={{ color: "#f59e0b", marginTop: 0 }}>
              Printify Integration
            </h3>

            <p
              style={{
                color: "#aaa",
                fontSize: "0.85rem",
                lineHeight: 1.5,
              }}
            >
              Import new Printify merchandise and synchronize existing product
              variants, availability, and mockup images.
            </p>

            <button
              type="button"
              className="fuel-primary-btn"
              onClick={handlePrintifySync}
              disabled={isSyncingPrintify}
              style={{
                width: "100%",
                padding: "0.8rem",
                cursor: isSyncingPrintify ? "not-allowed" : "pointer",
                opacity: isSyncingPrintify ? 0.6 : 1,
              }}
            >
              {isSyncingPrintify
                ? "🔄 Synchronizing Products..."
                : "🔄 Sync Printify Products"}
            </button>

            {isSyncingPrintify && (
              <p style={{ color: "#f59e0b", fontSize: "0.8rem" }}>
                Connecting to Printify and updating your catalog. Please wait...
              </p>
            )}

            {printifySyncError && (
              <div className="notes-alert notes-alert--error">
                {printifySyncError}
              </div>
            )}

            {printifySyncResult && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "0.9rem",
                  background: "#0b0b0b",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  color: "#ddd",
                }}
              >
                <strong style={{ color: "#10b981" }}>
                  Synchronization Completed
                </strong>

                <p>Products Found: {printifySyncResult.total}</p>
                <p>Successfully Synced: {printifySyncResult.successful}</p>
                <p>Failed: {printifySyncResult.failed}</p>

                {printifySyncResult.syncedAt && (
                  <p>
                    Last Sync:{" "}
                    {new Date(printifySyncResult.syncedAt).toLocaleString()}
                  </p>
                )}

                {printifySyncResult.failures?.length > 0 && (
                  <div style={{ color: "#f87171" }}>
                    {printifySyncResult.failures.map((failure) => (
                      <p key={failure.productId}>
                        {failure.name}: {failure.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="list-panel-header">
            <h3>Inventory Catalog Feed ({productsList.length})</h3>
          </div>

          <div className="notes-scroll-stack">
            {productsList.map((g) => {
              const isPrintify = g.fulfillmentMethod === "printify";
              const availableCount = (g.variants || []).filter(
                (variant) => variant.isAvailable === true
              ).length;
              const thumbnail =
                g.images?.[0] ||
                g.variants?.find((variant) => variant.colorImages?.length)
                  ?.colorImages?.[0] ||
                "https://placehold.co/80x80?text=Product";

              return (
                <article
                  key={g._id}
                  className={`gear-catalog-row-card${
                    selectedPrintifyId === g._id ? " is-selected" : ""
                  }`}
                >
                  <div className="catalog-row-main">
                    <img src={thumbnail} alt="" className="catalog-row-thumb" />
                    <div className="catalog-row-info">
                      <strong title={g.name}>{g.name}</strong>
                      <span className="catalog-category">
                        {g.category || "Uncategorized"}
                      </span>
                      <div className="badges-line">
                        {isPrintify ? (
                          <>
                            <span className="store-badge store-badge--printify">
                              Printify
                            </span>
                            <span
                              className={`store-badge ${
                                g.isPublished
                                  ? "store-badge--published"
                                  : "store-badge--unpublished"
                              }`}
                            >
                              {g.isPublished ? "Published" : "Unpublished"}
                            </span>
                          </>
                        ) : (
                          <span className="store-badge store-badge--self">
                            Self managed
                          </span>
                        )}
                        {isPrintify && availableCount === 0 && (
                          <span className="store-badge store-badge--error">
                            No available variants
                          </span>
                        )}
                        {!isPrintify && !g.inStock && (
                          <span className="store-badge store-badge--error">
                            Out of stock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="catalog-row-controls">
                    {isPrintify ? (
                      <button
                        type="button"
                        className="catalog-manage-btn"
                        onClick={() => handleManagePrintify(g)}
                        aria-label={`Manage ${g.name}`}
                      >
                        <FaEdit /> Manage product
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="ctrl-btn"
                          onClick={() => handleEditSelect(g)}
                          title={`Edit ${g.name}`}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="ctrl-btn danger"
                          onClick={() => handleDelete(g._id)}
                          title={`Delete ${g.name}`}
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
